import { create } from 'zustand';
import { createContext, useContext } from 'react';

/** A single FPS sample — timestamp (ms) + value */
export type FpsSample = { t: number; v: number };

/** A single worker activity sample — timestamp (ms) + active/total counts */
export type WorkerSample = { t: number; active: number; total: number };

/**
 * Maximum age (ms) of retained history samples.
 * Samples older than this are pruned on each new push.
 */
const HISTORY_WINDOW_MS = 10_000;

/** Append a sample to a history array, pruning entries older than HISTORY_WINDOW_MS. */
function pushSample<T extends { t: number }>(history: T[], sample: T): T[] {
  const cutoff = sample.t - HISTORY_WINDOW_MS;
  // Find the first index that is within the window to avoid O(n) splice
  let start = 0;
  while (start < history.length && history[start].t < cutoff) start++;
  const trimmed = start === 0 ? history : history.slice(start);
  return [...trimmed, sample];
}

interface PerformanceState {
  fps: number;
  triangles: number;
  drawCalls: number;
  setPerformance: (stats: {
    fps: number;
    triangles: number;
    drawCalls: number;
  }) => void;

  /** Rolling 10-second history of FPS samples (one per ~500 ms tick) */
  fpsHistory: FpsSample[];
  pushFpsSample: (sample: FpsSample) => void;

  /** Compile workers (pool + main worker) */
  compileWorkers: { active: number; total: number };
  setCompileWorkerStats: (stats: { active: number; total: number }) => void;

  /** Texture script workers (dynamic, 0-N) */
  textureWorkers: { active: number; total: number };
  setTextureWorkerStats: (stats: { active: number; total: number }) => void;

  /** Rolling 10-second history of combined worker activity samples */
  workerHistory: WorkerSample[];
  pushWorkerSample: (sample: WorkerSample) => void;

  /**
   * The dominant color of the currently rendered model (rgba, 0-1 range).
   * Used to tint the worker activity graph. Null when no model is loaded.
   */
  modelColor: [number, number, number, number] | null;
  setModelColor: (color: [number, number, number, number] | null) => void;
}

type PerformanceStore = ReturnType<typeof createPerformanceStore>;

function createPerformanceStore() {
  return create<PerformanceState>((set) => ({
    fps: 0,
    triangles: 0,
    drawCalls: 0,
    setPerformance: (stats) => set(stats),

    fpsHistory: [],
    pushFpsSample: (sample) =>
      set((state) => ({ fpsHistory: pushSample(state.fpsHistory, sample) })),

    compileWorkers: { active: 0, total: 0 },
    setCompileWorkerStats: (stats) => set({ compileWorkers: stats }),

    textureWorkers: { active: 0, total: 0 },
    setTextureWorkerStats: (stats) => set({ textureWorkers: stats }),

    workerHistory: [],
    pushWorkerSample: (sample) =>
      set((state) => ({
        workerHistory: pushSample(state.workerHistory, sample),
      })),

    modelColor: null,
    setModelColor: (color) => set({ modelColor: color }),
  }));
}

/**
 * Context that holds a per-viewer Zustand store instance.
 * Each ThreeScene (and thus each ScadPreview) creates its own store so that
 * performance updates in one viewer do not trigger re-renders in others.
 */
export const PerformanceStoreContext = createContext<PerformanceStore | null>(
  null,
);

export function createViewerPerformanceStore() {
  return createPerformanceStore();
}

/**
 * Hook for consuming the per-viewer performance store.
 * Falls back to a module-level singleton for backwards compatibility
 * (e.g., when used outside of a PerformanceStoreContext provider).
 */
const fallbackStore = createPerformanceStore();

export function usePerformanceStore<T>(
  selector: (state: PerformanceState) => T,
): T {
  const store = useContext(PerformanceStoreContext) ?? fallbackStore;
  return store(selector);
}
